import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, CalendarHeart, Cake, Gift, TrendingUp } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { WishlistCard } from "@/components/WishlistCard";
import { WishlistDialog } from "@/components/WishlistDialog";
import { useLang } from "@/lib/i18n";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch (err) {
      console.error("dashboard load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const bdayDays = (() => {
    if (!user?.birthday) return null;
    const bd = new Date(user.birthday);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let next = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
    if (next < today) next = new Date(today.getFullYear() + 1, bd.getMonth(), bd.getDate());
    return Math.round((next - today) / 86400000);
  })();
  const showBdayBanner = bdayDays !== null && bdayDays <= 14 && user?.username;
  const bdayWaHref = `https://wa.me/?text=${encodeURIComponent(t("bday_wa_msg", { link: `${window.location.origin}/${user?.username}` }))}`;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {showBdayBanner && (
        <div className="bg-gradient-to-r from-indigo-600 to-fuchsia-600 rounded-3xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 fade-up shadow-[0_8px_24px_rgba(99,102,241,0.25)]" data-testid="birthday-share-banner">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <Cake size={20} className="text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-white">
                {bdayDays === 0 ? t("bday_banner_today") : t("bday_banner_t")}
                {bdayDays > 0 && <span className="ml-2 text-white/80 text-sm font-medium">({t("bday_days_left_note", { d: bdayDays })})</span>}
              </p>
              <p className="text-white/80 text-sm mt-0.5">{t("bday_banner_d")}</p>
            </div>
          </div>
          <a href={bdayWaHref} target="_blank" rel="noopener noreferrer" data-testid="birthday-share-wa-btn"
            className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white text-sm font-semibold active:scale-95 transition-all">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
            {t("bday_share_wa")}
          </a>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t("dash_greet")}, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("dash_sub")}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setDialogOpen(true)} className="rounded-full active:scale-95 transition-transform" data-testid="dashboard-new-wishlist-btn">
            <Plus size={16} className="mr-1" /> {t("new_list")}
          </Button>
        </div>
      </div>

      {(data.upcoming_events.length > 0 || data.friend_birthdays.length > 0) && (
        <section>
          <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><CalendarHeart size={18} className="text-primary" /> {t("upcoming")}</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {data.upcoming_events.map((ev) => (
              <Link key={ev.wishlist_id} to={`/liste/${ev.wishlist_id}`}
                className="shrink-0 bg-primary text-white rounded-2xl p-5 min-w-[200px] card-lift" data-testid={`upcoming-event-${ev.wishlist_id}`}>
                <p className="font-heading font-semibold">{ev.title}</p>
                <p className="text-white/80 text-sm mt-1">{ev.days_left === 0 ? t("today_party") : `${ev.days_left} ${t("days_left")}`}</p>
              </Link>
            ))}
            {data.friend_birthdays.map((f) => (
              <Link key={f.user_id} to={`/${f.username}`}
                className="shrink-0 bg-card border border-border/50 rounded-2xl p-4 min-w-[210px] flex items-center gap-3 card-lift" data-testid={`friend-birthday-${f.username}`}>
                <Avatar className="w-11 h-11">
                  <AvatarImage src={f.picture} />
                  <AvatarFallback className="bg-secondary">{f.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-sm">{f.name}</p>
                  <p className="text-xs text-primary flex items-center gap-1 mt-0.5"><Cake size={12} /> {f.days_to_birthday === 0 ? t("today") : `${f.days_to_birthday} ${t("days_left")}`}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Gift size={18} className="text-primary" /> {t("my_wishlists")}</h2>
          <Link to="/listelerim" className="text-sm text-primary font-semibold" data-testid="dashboard-all-wishlists-link">{t("see_all")}</Link>
        </div>
        {data.wishlists.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center">
            <p className="text-muted-foreground mb-4">{t("dash_no_lists")}</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full" data-testid="dashboard-empty-create-btn">
              <Plus size={16} className="mr-1" /> {t("create_list")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.wishlists.slice(0, 6).map((wl, i) => <WishlistCard key={wl.wishlist_id} wishlist={wl} delay={i * 0.05} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-primary" /> {t("popular")}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {data.popular_gifts.map((p, i) => (
            <Link to="/kesfet" key={p.product_id} className="bg-card rounded-2xl border border-border/50 overflow-hidden card-lift fade-up" style={{ animationDelay: `${i * 0.04}s` }}>
              <img src={p.image_url} alt={p.name} className="aspect-square w-full object-cover" />
              <div className="p-3">
                <p className="text-sm font-semibold truncate">{p.name}</p>
                <p className="text-sm text-primary font-bold">{formatPrice(p.price)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <WishlistDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </div>
  );
}
